import React, { useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { PAGE_VIEW_ROLES } from '@onboarding-dashboard/shared';
import { useAuth } from '../../contexts/AuthContext.js';
import homeTemplate from './home.html?raw';
import homeLogoUrl from '../../assets/eXeX_Title_White_with_name.png';
import './home.css';

export function HomePage(): React.ReactElement {
  const containerRef = useRef<HTMLDivElement>(null);
  const navigate = useNavigate();
  const { user, hasRole } = useAuth();

  // Redirect external-only users to their primary page
  useEffect(() => {
    if (user && user.role === 'external') {
      const roles = user.roles ?? [];
      const canView = (page: keyof typeof PAGE_VIEW_ROLES): boolean =>
        PAGE_VIEW_ROLES[page].some((r) => roles.includes(r));
      const hasMC = canView('missionControl');
      const hasCC = canView('commandCenter');
      const hasInsights = canView('insights');
      const hasCanopus = canView('canopusCommand');
      navigate(
        hasMC
          ? '/mission-control'
          : hasCC
            ? '/command-center'
            : hasInsights
              ? '/insights'
              : hasCanopus
                ? '/canopus-command'
                : '/',
        { replace: true }
      );
    }
  }, [user, navigate]);

  useEffect(() => {
    const container = containerRef.current;
    if (!container) return;

    // Set logo src
    const logoEl = container.querySelector<HTMLImageElement>('#home-logo');
    if (logoEl) logoEl.src = homeLogoUrl;

    const canViewHiring =
      hasRole('admin') || hasRole('hr') || hasRole('manager') || hasRole('employee');

    // Show hiring pipeline cards for admin/hr/manager
    const pipelineCard = container.querySelector<HTMLElement>('#pipeline-card');
    if (pipelineCard) pipelineCard.style.display = canViewHiring ? '' : 'none';

    const rolesCard = container.querySelector<HTMLElement>('#roles-card');
    if (rolesCard) rolesCard.style.display = canViewHiring ? '' : 'none';

    // Show admin-only card
    const membersCard = container.querySelector<HTMLElement>('#manage-members-card');
    if (membersCard) membersCard.style.display = hasRole(PAGE_VIEW_ROLES.members) ? '' : 'none';

    // Show Mission Control and Insights cards for MC users
    const canViewMC = hasRole(PAGE_VIEW_ROLES.missionControl);
    const mcCard = container.querySelector<HTMLElement>('#mission-control-card');
    if (mcCard) mcCard.style.display = canViewMC ? '' : 'none';

    const canViewInsights = hasRole(PAGE_VIEW_ROLES.insights);
    const insightsCard = container.querySelector<HTMLElement>('#insights-card');
    if (insightsCard) insightsCard.style.display = canViewInsights ? '' : 'none';

    const canViewCC = hasRole(PAGE_VIEW_ROLES.commandCenter);
    const ccCard = container.querySelector<HTMLElement>('#command-center-card');
    if (ccCard) ccCard.style.display = canViewCC ? '' : 'none';

    const canViewCanopus = hasRole(PAGE_VIEW_ROLES.canopusCommand);
    const canopusCard = container.querySelector<HTMLElement>('#canopus-command-card');
    if (canopusCard) canopusCard.style.display = canViewCanopus ? '' : 'none';

    const canViewBrandBook = hasRole(PAGE_VIEW_ROLES.brandBook);
    const brandBookCard = container.querySelector<HTMLElement>('#brand-book-card');
    if (brandBookCard) brandBookCard.style.display = canViewBrandBook ? '' : 'none';

    // Make all feature cards navigate via React Router
    const cards = container.querySelectorAll<HTMLAnchorElement>('.feature-card');
    const cleanups: (() => void)[] = [];

    for (const card of cards) {
      const handler = (e: Event): void => {
        e.preventDefault();
        const href = card.getAttribute('href');
        if (href) navigate(href);
      };
      card.addEventListener('click', handler);
      cleanups.push(() => card.removeEventListener('click', handler));
    }

    return () => {
      cleanups.forEach((fn) => fn());
    };
  }, [hasRole, navigate]);

  return <div ref={containerRef} dangerouslySetInnerHTML={{ __html: homeTemplate }} />;
}
